// Utility: uploadUtils
// Purpose: Upload functions for survey video submission (GCS and Firebase)

import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';

// Get storage from the already initialized app
const storage = getStorage();

// Function to upload a file directly to Google Cloud Storage
export const uploadToGCS = (file, url, setUploadProgress, activeXhrRef, processingCancelledRef) => {
  return new Promise((resolve, reject) => {
    // Create a new XMLHttpRequest for direct upload
    const xhr = new XMLHttpRequest();
    
    // Store the XHR in our ref so we can abort it if needed
    if (activeXhrRef) {
      activeXhrRef.current = xhr;
    }
    
    // Setup upload progress tracking
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(progress);
      }
    };
    
    // Handle completion
    xhr.onload = () => {
      // Clean up the reference
      if (activeXhrRef) {
        activeXhrRef.current = null;
      }
      
      // Check for cancellation
      if (processingCancelledRef?.current) {
        console.log('Upload was cancelled, ignoring completion');
        return reject(new Error('Upload cancelled'));
      }
      
      // Handle success (GCS returns status 200 or 201 when successful)
      if (xhr.status >= 200 && xhr.status < 300) {
        console.log('GCS upload successful');
        resolve(url); // Resolve with the upload URL which is now the video URL
      } else {
        console.error('GCS upload failed:', xhr.status, xhr.statusText);
        reject(new Error(`GCS upload failed with status ${xhr.status}`));
      }
    };
    
    // Handle errors 
    xhr.onerror = () => {
      // Clean up the reference
      if (activeXhrRef) {
        activeXhrRef.current = null;
      }
      
      // Check for cancellation
      if (processingCancelledRef?.current) {
        console.log('Upload was cancelled, error event expected');
        return reject(new Error('Upload cancelled'));
      }
      
      console.error('Network error during GCS upload');
      reject(new Error('Network error during file upload'));
    };
    
    // Handle abortion
    xhr.onabort = () => {
      console.log('GCS upload was aborted');
      if (activeXhrRef) {
        activeXhrRef.current = null;
      }
      reject(new Error('Upload aborted'));
    };
    
    // Open the request with PUT method (required for GCS signed URLs)
    xhr.open('PUT', url, true);
    
    // Log the URL and file details for debugging
    console.log('Upload URL:', url);
    console.log('File type:', file.type);
    console.log('File size:', file.size);
    
    // Set minimal headers to match CORS configuration
    // Don't set Content-Type explicitly - let the browser set it
    // This helps avoid CORS preflight issues
    if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }
    
    // Only set essential headers that are allowed in CORS config
    // Remove headers that might cause CORS issues
    
    // Send the file as binary data
    xhr.send(file);
  });
};

// Function to upload using Firebase SDK (fallback)
export const uploadToFirebase = (file, uploadPath, setUploadProgress, activeXhrRef) => {
  return new Promise((resolve, reject) => {
    console.log('Attempting upload using Firebase SDK to:', uploadPath);
    
    // Extract the file path from the signed URL
    // Example URL: https://storage.googleapis.com/refrral-qa.firebasestorage.app/videos/subFAADXEzHtChw96lGC/surveyUpload_123.mp4?...
    let videoPath;
    
    try {
      // Try to extract path from the URL
      const urlObj = new URL(uploadPath);
      const pathParts = urlObj.pathname.split('/');
      
      // Find the videos part and extract everything after it
      const videosIndex = pathParts.findIndex(part => part === 'videos');
      if (videosIndex !== -1 && videosIndex < pathParts.length - 1) {
        // Get the path after 'videos/'
        videoPath = pathParts.slice(videosIndex + 1).join('/');
      } else {
        // Fallback: extract from the entire path
        const match = uploadPath.match(/\/videos\/(.+?)(\?|$)/);
        if (match) {
          videoPath = match[1];
        } else {
          throw new Error('Could not extract video path from URL');
        }
      }
      
      console.log('Extracted path:', videoPath);
    } catch (error) {
      console.error('Could not extract path from URL:', uploadPath, error);
      return reject(new Error('Invalid upload path'));
    }
    
    // Create storage reference using the extracted path
    const storageRef = ref(storage, `videos/${videoPath}`);
    
    // Create upload task with metadata
    const metadata = {
      contentType: file.type || 'video/mp4',
      customMetadata: {
        'originalName': file.name || 'video.mp4'
      }
    };
    
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);
    
    // Store the task in our ref so we can cancel it if needed
    if (activeXhrRef) {
      activeXhrRef.current = {
        abort: () => uploadTask.cancel()
      };
    }
    
    // Listen for state changes
    uploadTask.on('state_changed', 
      (snapshot) => {
        // Get upload progress
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        // Handle errors
        console.error('Firebase upload error:', error);
        if (activeXhrRef) {
          activeXhrRef.current = null;
        }
        reject(error);
      },
      () => {
        // Upload completed successfully
        console.log('Firebase upload successful');
        if (activeXhrRef) {
          activeXhrRef.current = null;
        }
        resolve(uploadPath);
      }
    );
  });
};

// Function to notify server about successful upload and submit metadata
export const notifyServerOfUpload = async (videoUrl, metadata, campaignId, permanentUploadPath, SERVER_URL) => {
  try {
    // Prepare the payload with all metadata and the permanent video path
    const payload = {
      campaignId: campaignId,
      firstName: metadata.firstName,
      lastName: metadata.lastName,
      email: metadata.email,
      zipCode: metadata.zipCode,
      startTime: metadata.startTime,
      videoDuration: metadata.videoDuration,
      originalVideoSize: metadata.originalVideoSize,
      compressedVideoSize: metadata.compressedVideoSize,
      videoUrl: permanentUploadPath || videoUrl // Use the permanent path if available, fallback to the URL
    };
    
    console.log('Submitting survey with permanent path:', permanentUploadPath);
    
    // Make the API call to record the metadata
    const response = await fetch(`${SERVER_URL}/survey/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    // Parse the response
    const data = await response.json();
    
    // Check for successful response
    if (response.status === 201) {
      console.log('Survey metadata recorded successfully:', data);
      return data;
    } else if (data.surveyFull) {
      console.log('Survey is full:', data);
      throw new Error('This survey is full. No more responses are accepted.');
    } else {
      console.error('Failed to record metadata:', data);
      throw new Error(data.error || 'Failed to record survey metadata');
    }
  } catch (error) {
    console.error('Error submitting metadata:', error);
    throw error;
  }
};


